'use strict';

// external imports
import React, {
    useCallback,
    useLayoutEffect,
    useMemo,
    useRef
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import parse from 'html-react-parser';
import moment from 'moment-timezone';
import {keys} from 'ts-transformer-keys';

// prime react imports
import {Card} from 'primereact/card';
import {Checkbox} from 'primereact/checkbox';
import {
    FileUpload as PrimeFileUpload,
    FileUploadHeaderTemplateOptions,
    FileUploadSelectEvent
} from 'primereact/fileupload';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import {formatBytes} from '~/shared/src/utilities/valueFormaters';

// frontend imports
import FileUploadToolbar from '~/frontend/src/components/base/XmlImport/FileUpload/Toolbar';
import {
    ComponentProps as ParentComponentProps,
    AllProps as ParentAllProps
} from '~/frontend/src/modules/panels/XmlImport';

// action imports
import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import {setElusiveData as setElusiveDataOrig} from '~/frontend/src/actions/elusiveData';


type PanelElusiveData = PanelErrorData & { xml : string[]; };

export type DefaultProps = {};
export const defaultProps : Required<DefaultProps> = {};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    value : Required<ParentComponentProps>['fileUploadValue'];
    onValueChange : ( value : Required<ParentComponentProps>['fileUploadValue'] ) => void;

    log : Required<Required<ParentAllProps>['elusiveData']>['log']['fileUpload'];
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;

type InternalState = {
    fileUploadXml : string[];
};

const setElusiveData = setElusiveDataOrig as ( panelKey : string, data : Partial<PanelElusiveData> ) => ReturnType<typeof setElusiveDataOrig>;

const FileUpload = ( props : AllProps ) => {
    const dispatch : AppDispatch        = useDispatch(),
          {
              uid,
              value,
              onValueChange,
              log
          }                             = props,
          user                          = useSelector( ( state : StoreState ) => state.session?.user ),
          language                      = user ? user.language : undefined,
          fileUploadRef                 = useRef<PrimeFileUpload>( null ),
          internalState : InternalState = useMemo(
              () => {
                  const retVal : InternalState = {
                      fileUploadXml: []
                  };

                  for ( const file of value ) {
                      if ( file.selected ) {
                          retVal.fileUploadXml.push( file.content );
                      }
                  }

                  return retVal;
              },
              [
                  value
              ]
          ),
          onToolbarOpenFile             = () => {
              fileUploadRef.current?.getInput()?.click();
          },
          onToolbarToggleSelection      = useCallback(
              () => {
                  const newValue    = ( [] as typeof value ).concat( value ),
                        allSelected = newValue.every( f => f.selected );

                  for ( let i = 0; i < newValue.length; i++ ) {
                      newValue[ i ] = {
                          ...newValue[ i ],
                          selected: !allSelected
                      };
                  }

                  onValueChange( newValue );
              },
              [
                  value,
                  onValueChange
              ]
          ),
          onToolbarDelete               = useCallback(
              () => {
                  const newValue = ( [] as typeof value ).concat( value ),
                        newFiles = ( [] as ( File & { objectURL : string } )[] ).concat( ( fileUploadRef.current?.getFiles() ?? [] ) as ( File & { objectURL : string } )[] );

                  for ( let i = newValue.length - 1; i >= 0; i-- ) {
                      if ( newValue[ i ].selected ) {
                          for ( let k = newFiles.length - 1; k >= 0; k-- ) {
                              if ( newFiles[ k ].objectURL === newValue[ i ].id ) {
                                  newFiles.splice( k, 1 );
                                  break;
                              }
                          }

                          newValue.splice( i, 1 );
                      }
                  }

                  fileUploadRef.current?.setFiles( newFiles );
                  onValueChange( newValue );
              },
              [
                  value,
                  onValueChange
              ]
          ),
          onFileUploadSelect            = useCallback(
              ( event : FileUploadSelectEvent ) => {
                  const newValue       = ( [] as typeof value ).concat( value ),
                        reader         = new FileReader(),
                        asyncReadFile  = async ( file : File ) : Promise<string> => {
                            let resolveFnc : ( content : string ) => void;

                            const retVal : Promise<string> = new Promise( ( resolve ) => {
                                resolveFnc = resolve;
                            } );

                            reader.onloadend = () => {
                                resolveFnc( reader.result as string );
                            };

                            reader.readAsText( file );

                            return retVal;
                        },
                        asyncReadFiles = async () => {
                            for ( const file of event.files as ( File & { objectURL : string } )[] ) {
                                if ( !newValue.find( nV => nV.id === file.objectURL ) ) {
                                        newValue.push( {
                                            id      : file.objectURL,
                                            name    : file.name,
                                            size    : file.size,
                                            content : await asyncReadFile( file ),
                                            selected: true,
                                            status  : 'pending'
                                        } );
                                }
                            }

                            onValueChange( newValue );
                        };

                      void asyncReadFiles();
              },
              [
                  value,
                  onValueChange
              ]
          ),
          onFileToggleSelection         = useCallback(
              ( id : string ) => {
                  const newValue = ( [] as typeof value ).concat( value ),
                        index    = newValue.findIndex( f => f.id === id );

                  if ( index !== -1 ) {
                      newValue[ index ] = {
                          ...newValue[ index ],
                          selected: !newValue[ index ].selected
                      };

                      onValueChange( newValue );
                  }
              },
              [
                  value,
                  onValueChange
              ]
          ),
          onDragOver                    = ( event : React.DragEvent<HTMLDivElement> ) => {
              if ( event.dataTransfer?.dropEffect ) {
                  event.dataTransfer.dropEffect = 'copy';
              }
              event.stopPropagation();
              event.preventDefault();
          },
          onDragLeave                   = ( event : React.DragEvent<HTMLDivElement> ) => {
              if ( event.dataTransfer?.dropEffect ) {
                  event.dataTransfer.dropEffect = 'none';
              }
          },
          onDrop                        = useCallback(
              ( event : React.DragEvent<HTMLDivElement> ) => {
                  event.stopPropagation();
                  event.preventDefault();

                  fileUploadRef.current?.onFileSelect( event as unknown as FileUploadSelectEvent );
              },
              [
                  onFileUploadSelect
              ]
          ),
          toolbar                       = useMemo(
              () => {
                  return <FileUploadToolbar
                      uid={`${uid}_fileUploadToolbar`}

                      onSelectFile={onToolbarOpenFile}
                      toggleSelectionButtonDisabled={!value.length}
                      onToggleSelection={onToolbarToggleSelection}
                      deleteButtonDisabled={!value.some( f => f.selected )}
                      onDelete={onToolbarDelete}
                  />;
              },
              [
                  uid,
                  value,
                  onFileToggleSelection,
                  onToolbarDelete
              ]
          ),
          headerTemplate                = useCallback(
              ( options : FileUploadHeaderTemplateOptions ) => {
                  let content : React.ReactNode = <div className="flex align-items-center justify-content-center flex-column yn-filefield-empty" style={{ width: '100%', height: '100%' }}>
                      <i className="pi pi-file mt-3 p-5" style={{ fontSize: '4rem', borderRadius: '50%', backgroundColor: 'var(--surface-b)', color: 'var(--surface-d)' }}/>
                      <span className="my-5" style={{ fontSize: '1.2rem', color: 'var(--text-color-secondary)' }}>
                                                    {__( 'YN.base.form.fileField.dragndrop.label' )}
                                                </span>
                  </div>;

                  if ( value.length ) {
                      const fileList : React.ReactNode[] = [];

                      for ( let i = 0; i < value.length; i++ ) {
                          const file = value[ i ];

                          fileList.push( <div key={`entry_${i}`} className="p-fileupload-row">
                              <Checkbox
                                  checked={file.selected}
                                  onChange={onFileToggleSelection.bind( null, file.id )}
                                  style={{ flex: '0 0 auto', width: 'auto' }}
                              />
                              <div className="p-fileupload-filename">
                                  {file.name}
                              </div>
                              <div>
                                  {formatBytes( file.size )}
                              </div>
                              <div className={`yn-import-status-${file.status}`}>
                                  {parse( __( `YN.base.xmlImport.fileUpload.fileStatus.${file.status}.label` ) )}
                              </div>
                          </div> );
                      }

                      content = <div className="p-fileupload-list">
                          {fileList}
                      </div>;
                  }

                  return <>
                      <div className="hidden">
                          {options.chooseButton}
                      </div>
                      <div
                          className="yn-fileupload-content"
                          style={{ flex: 1, overflowX: 'hidden', overflowY: 'auto' }}

                          onDragOver={onDragOver}
                          onDragLeave={onDragLeave}
                          onDrop={onDrop}
                      >
                          {content}
                      </div>
                  </>;
              },
              [
                  value,
                  onFileToggleSelection,
                  onDrop,
                  language
              ]
          ),
          itemTemplate                  = () : React.ReactNode => {
              return undefined;
          },
          input                         = useMemo(
              () => {
                  return <div
                      key={`${uid}_fileUploadField`}
                      className="yn-content-panel-container yn-flex-layout-parent"
                      style={{ flex: '1 1 auto' }}
                  >
                      <Card className="yn-flex-layout-parent" style={{ flex: '1 1 auto' }}>
                          <PrimeFileUpload
                              ref={fileUploadRef}
                              id={`${uid}_fileUpload`}

                              auto={false}
                              multiple={true}
                              accept="application/xml, text/xml"
                              maxFileSize={2000000}

                              onSelect={onFileUploadSelect}

                              headerTemplate={headerTemplate}
                              itemTemplate={itemTemplate}
                              emptyTemplate={undefined}

                              invalidFileSizeMessageSummary={__( 'YN.base.form.fileField.filesize.invalid.summary' )}
                              invalidFileSizeMessageDetail={__( 'YN.base.form.fileField.filesize.invalid.detail' )}
                              invalidFileAcceptMessageSummary={__( 'YN.base.form.fileField.accept.invalid.summary' )}
                              invalidFileAcceptMessageDetail={__( 'YN.base.form.fileField.accept.invalid.detail' )}

                              pt={{ root: { className: 'yn-xml-fileupload' } }}
                              style={{ flex: 1, display: 'flex' }}
                              contentStyle={{ display: 'none' }}
                          />
                      </Card>
                  </div>;
              },
              [
                  uid,
                  headerTemplate,
                  onFileUploadSelect,
                  language
              ]
          ),
          logPanel                      = useMemo(
              () => {
                  if ( log?.length ) {
                      const output : React.ReactNode[] = [],
                            indentLength : number      = 28;

                      for ( const logEntry of log ) {
                          let logEntryString = '';

                          logEntryString += `[${moment( logEntry.timestamp ).format( 'YYYY-MM-DD HH:mm:ss' )}]`;
                          logEntryString += `[<span class="yn-log-${logEntry.severity}">${logEntry.severity}</span>${'&nbsp;'.repeat( 7 - logEntry.severity.length )}]`;
                          logEntryString += `&nbsp;${logEntry.file}:`;
                          logEntryString += `&nbsp;${logEntry.message.replaceAll( ' ', '&nbsp;' ).split( '\n' ).join( '\n<br/>' + '&nbsp;'.repeat( indentLength ) )}`;
                          logEntryString += '\n<br/>';

                          output.push( parse( logEntryString ) );
                      }

                      return <div
                          key={`${uid}_logPanel`}
                          className="yn-content-panel-container yn-flex-layout-parent"
                          style={{ paddingLeft: 0, flex: '0 0 50rem', height: '100%' }}
                      >
                          <Card
                              style={{
                                  alignItems: 'stretch',
                                  display   : 'flex',
                                  flex      : 1,
                                  padding   : '1rem 2rem',
                                  height    : '100%'
                              }}
                              pt={{
                                  body   : {
                                      style: {
                                          padding: '0',
                                          width  : '100%'
                                      }
                                  },
                                  content: {
                                      style: {
                                          flex      : 1,
                                          overflow  : 'auto',
                                          whiteSpace: 'nowrap',
                                          height    : '100%',
                                          width     : '100%'
                                      }
                                  }
                              }}
                          >
                              <div className="yn-log">
                                  {output}
                              </div>
                              <div id={`${uid}_logPanel_bottom`} style={{ float: 'left', clear: 'both' }}/>
                          </Card>
                      </div>
                  }
                  else {
                      return null;
                  }
              },
              [
                  log
              ]
          );

    // scroll log to bottom
    useLayoutEffect(
        () => {
            document.getElementById( `${uid}_logPanel_bottom` )?.scrollIntoView( { behavior: 'smooth' } );
        }
    );

    useLayoutEffect(
        () => {
            dispatch( setElusiveData( uid, {
                xml: internalState.fileUploadXml
            } ) );
        },
        [
            dispatch,
            uid,
            internalState.fileUploadXml
        ]
    );

    return useMemo(
        () => {
            return <>
                {toolbar}
                <div className="yn-flex-layout-parent yn-flex-layout-child flex-row align-items-stretch">
                    {input}
                    {logPanel}
                </div>
            </>;
        },
        [
            toolbar,
            input,
            logPanel
        ]
    );
};
FileUpload.defaultProps = defaultProps;
FileUpload.displayName = 'XmlImport-FileUpload';

export default FileUpload;
